/**
 * Mutation hook for replacing the tenant's inactive-rules list for a
 * single component via `PUT /<module>/component-rules/inactive`.
 *
 * The client and `<module>` are resolved per call from the component id and
 * the nearest `IntegratedComponentProvider`: a component of the provider's
 * module goes through that module (e.g. `extranetApiClient` +
 * `/extranet/component-rules/inactive`); otherwise — or outside any
 * provider — through the historical claimflow client/path. Claimflow pages
 * therefore send the exact same request as before. See
 * `./rulesApiTarget.ts`.
 *
 * On success it invalidates every module's inactive-rules query (prefix
 * `['configurable', 'component-rules', 'inactive']`) so the HOC
 * re-evaluates with the fresh suppression set on the next render.
 *
 * Fail-noisy: unlike `useInactiveComponentRules` this hook does
 * **not** swallow axios errors. Callers (the `ToggleRulesButton` modal)
 * surface them inline.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { useIntegratedComponentContext } from '@components/integrated/shared/IntegratedComponentContext';

import {
  componentRulesPath,
  moduleOf,
  resolveRulesApiTarget,
} from './rulesApiTarget';
import { INACTIVE_RULES_QUERY_KEY_PREFIX } from './useInactiveComponentRules';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Variables accepted by the mutation.
 *
 * The backend expects `{ component_id, rule_ids }` — we take camelCase
 * at the call site and map inside the hook for symmetry with the rest
 * of the configurable runtime.
 */
export interface UpdateInactiveRulesVars {
  /** Registered component id this inactive-list replacement targets. */
  componentId: string;
  /** Full replacement list of inactive rule ids for the component. */
  ruleIds: string[];
}

/**
 * `UseMutationResult` specialisation — the mutation returns no data
 * meaningful to the caller, so the `TData` slot is `undefined`.
 */
export type UseUpdateInactiveRulesResult = UseMutationResult<
  undefined,
  unknown,
  UpdateInactiveRulesVars
>;

// ---------------------------------------------------------------------------
// Public hook
// ---------------------------------------------------------------------------

/**
 * Replace the tenant's inactive-rules list for a single component.
 *
 * On success the tenant-wide inactive-rules query key is invalidated so
 * any `withComponentRules` wrapper re-picks-up the new suppression set
 * without a full reload.
 */
export function useUpdateInactiveRules(): UseUpdateInactiveRulesResult {
  const queryClient = useQueryClient();
  const context = useIntegratedComponentContext();

  return useMutation<undefined, unknown, UpdateInactiveRulesVars>({
    mutationFn: async ({ componentId, ruleIds }) => {
      const target = resolveRulesApiTarget(context, moduleOf(componentId));
      await target.apiClient.put(`${componentRulesPath(target)}/inactive`, {
        component_id: componentId,
        rule_ids: ruleIds,
      });
      return undefined;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...INACTIVE_RULES_QUERY_KEY_PREFIX],
      });
    },
  });
}
